import { useMemo } from 'react';

import { NO_VALUE_SENTINEL } from '../../../../../services/contacts/filters';
import { useLazyGetContactsQuery } from '../../../../../services/contactsV2';

/**
 * Placeholder option id that is never assigned to contacts. Used when a board column cannot
 * match the active sidebar filter (e.g. No value column + Status=Sourced). OpenSearch `terms`
 * on a non-existent option returns zero hits.
 */
const KANBAN_NO_MATCH_OPTION_ID = '00000000-0000-4000-8000-000000000001';

function normalizeGlobalCustomFieldValues(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw
    .map((item) => {
      if (typeof item === 'object' && item !== null && 'value' in item) return item.value;
      return item;
    })
    .filter((v) => v != null && v !== '');
}

/**
 * Merges sidebar custom-field filters with the board column's status field without sending two
 * `custom_fields` entries for the same field id. The API groups by id and ORs values; mixing
 * `{ id }` (unassigned) with `{ id, value }` (sidebar) yields "unassigned OR sourced" and
 * duplicates cards across columns.
 */
export function buildKanbanColumnFilters(customFieldId, columnValue, globalFilters = {}) {
  const filterKey = `customFields_${customFieldId}`;
  const { [filterKey]: _removed, ...restGlobal } = globalFilters;
  const globalValues = normalizeGlobalCustomFieldValues(globalFilters[filterKey]);
  const asKey = (v) => String(v);

  let columnCustomFields;
  if (globalValues.length === 0) {
    if (columnValue != null && columnValue !== '') {
      columnCustomFields = [{ id: customFieldId, value: columnValue }];
    } else {
      columnCustomFields = [{ id: customFieldId }];
    }
  } else {
    const globalSet = new Set(globalValues.map(asKey));
    if (columnValue != null && columnValue !== '') {
      columnCustomFields = globalSet.has(asKey(columnValue))
        ? [{ id: customFieldId, value: columnValue }]
        : [{ id: customFieldId, value: KANBAN_NO_MATCH_OPTION_ID }];
    } else {
      columnCustomFields = globalSet.has(NO_VALUE_SENTINEL)
        ? [{ id: customFieldId }]
        : [{ id: customFieldId, value: KANBAN_NO_MATCH_OPTION_ID }];
    }
  }

  return {
    ...restGlobal,
    customFields: columnCustomFields,
  };
}

export const argsForColumn = ({ customFieldId, listId, value, globalFilters }) => ({
  listId,
  contextualizedToSharedListId: listId,
  filters: buildKanbanColumnFilters(customFieldId, value, globalFilters ?? {}),
  board: true,
});

export const useKanbanColumns = ({ customField, listId, queryParams }) => {
  const columns = useMemo(() => {
    if (!customField) return [];

    const columnOptions = customField.params?.options || [];
    const columnDefinitions = columnOptions.map((option) => ({
      id: `column:${option.id}`,
      title: option.label,
      status: option.id,
      meta: {
        customField,
        argsForColumn: argsForColumn({
          customFieldId: customField.id,
          listId,
          value: option.id,
          globalFilters: queryParams.filters,
        }),
        query: useLazyGetContactsQuery,
      },
    }));

    columnDefinitions.unshift({
      id: 'column:unassigned',
      title: 'No value',
      status: null,
      meta: {
        customField,
        argsForColumn: argsForColumn({
          customFieldId: customField.id,
          listId,
          value: null,
          globalFilters: queryParams.filters,
        }),
        query: useLazyGetContactsQuery,
      },
    });

    return columnDefinitions;
  }, [customField, listId, queryParams.filters]);

  return { columns };
};
